import { Achievement, AchievementService } from './../../../shared/services/achievement.service';
import { ExerciseService } from './../../../shared/services/exercises.service';
import * as moment from 'moment-timezone';
import { Component, Inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatSnackBar, MatTableDataSource } from '@angular/material';
import { Program } from 'app/shared/models/Program';
import { Exercise } from 'app/shared/models/Exercises';
import { VideoPlayerComponent } from 'app/theme/components/video-player/video-player.component';
import { AddDialogComponent } from '../add-dialog/add-dialog.component';
import { IFilteredStats, StatisticService } from '../../../shared/services/statistic.service';
import { Stat } from '../../../shared/models/Stat';
import { TranslateService } from '@ngx-translate/core';
import { ChallengeService } from 'app/shared/services/challenge.service';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { Subject } from 'rxjs';
import { Category } from 'app/shared/models/Category';
import { SuccessDialogComponent } from '../achievement/components/success-dialog/success-dialog.component';
import { UserService } from 'app/shared/services/user.service';

interface Element {
  alias: string;
  position: number;
  points: number;
  isRequester: boolean;
}
@Component({
  selector: 'app-video-dialog',
  templateUrl: './videoDialog.component.html',
  styleUrls: ['./videoDialog.component.scss'],
})
export class VideoDialogComponent implements OnDestroy {
  program: Program;
  isSingleVideo: boolean = false;
  currentExerciseIndex: number = 0;
  canGoBack: boolean = false;
  allExerciseSize = 0;
  nextExerciseCalled: boolean = false;

  didSentStatistic: boolean = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  private takesPartInTeamChallenge = false;

  private achievementsProcessing = false;

  // check if achievments are activated for company
  isAchievementsActivated = false;

  @ViewChild('player') videoPlayer: VideoPlayerComponent;

  constructor(
    public dialogRef: MatDialogRef<VideoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private stats: StatisticService,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    private dialog: MatDialog,
    private challengeService: ChallengeService,
    private achievementService: AchievementService,
    private userService: UserService
  ) {
    if (data.program) {
      this.program = data.program as Program;
    } else if (data.exercise) {
      const ex = data.exercise as Exercise;
      this.program = new Program();
      this.program.exercises = [ex];
      this.program.exerciseIds = [ex._id];
      this.program.title = ex.title['de'];
      this.isSingleVideo = true;
      this.nextExerciseCalled = false;
    }

    this.getCompanyData();

    this.canGoBack = data.canGoBack;
    this.allExerciseSize = data.sizeAllExercise;

    // Find out whether toast for scored points should be displayed
    combineLatest(this.challengeService.displayedChallenge, this.challengeService.team, this.challengeService.teamUser)
      .takeUntil(this.ngUnsubscribe)
      .subscribe(([challenge, team, user]) => {
        if (
          // If a challenge is currently running and the user is member of a team and the team has enough members for the challenge
          challenge &&
          moment(challenge.start).isBefore(moment()) &&
          moment(challenge.end).isAfter(moment()) &&
          user &&
          user.membershipStatus === 'member' &&
          team &&
          team.userGroups.members.length >= challenge.minimalTeamMembers
        ) {
          this.takesPartInTeamChallenge = true;
        } else {
          this.takesPartInTeamChallenge = false;
        }
      });
  }

  async getCompanyData() {
    const companyData: any = await this.userService.getCompany();
    this.isAchievementsActivated = companyData.activateAchievements;
  }

  skipTo(exercise: Exercise) {
    this.currentExerciseIndex = this.program.exercises.indexOf(exercise);

    this.videoPlayer.changeUrl(
      this.program.exercises[this.currentExerciseIndex].videoUrl['de'],
      this.program.exercises[this.currentExerciseIndex].captionUrl[this.translate.currentLang]
    );
    this.didSentStatistic = false;
  }

  videoProgress(event) {
    if (event.value > 15 && !this.didSentStatistic) {
      this.didSentStatistic = true;
      this.stats.sendDidPlayVideoStatistic(
        new Stat(this.program.exerciseIds[this.currentExerciseIndex], this.program.contextID)
      );
    }
  }

  ELEMENT_DATA: Element[] = [
    { position: 0, alias: '...', points: 0, isRequester: false },
    { position: 1, alias: '...', points: 0, isRequester: false },
    { position: 2, alias: '...', points: 0, isRequester: false },
    { position: 3, alias: '...', points: 0, isRequester: false },
    { position: 4, alias: '...', points: 0, isRequester: false },
    { position: 5, alias: '...', points: 0, isRequester: false },
  ];

  dataSource = new MatTableDataSource(this.ELEMENT_DATA);

  countUnique(iterable) {
    return new Set(iterable).size;
  }

  async videoEnded(e) {
    if (this.isAchievementsActivated && !this.achievementsProcessing) {
      this.achievementsProcessing = true;
      let newUnlockedAchievements = await this.achievementService.processAchievementData(
        this.program,
        this.allExerciseSize,
        this.nextExerciseCalled,
        this.currentExerciseIndex
      );

      // Check B2Action Hero separately to ensure it triggers on the 5th day
      // This is especially important when completing the 4th exercise of a day
      const heroAchievement = await this.achievementService.checkB2ActionHeroAchievement();
      if (Object.keys(heroAchievement).length > 0) {
        newUnlockedAchievements.push(heroAchievement);
      }

      const newArray = newUnlockedAchievements.filter((value) => Object.keys(value).length !== 0);
      if (newArray.length > 0) {
        this.openSuccessDialog(newArray[0]);
      }
      this.achievementsProcessing = false;
    }

    this.stats.getMyStatistic().then((stats) => {
      if (!this.takesPartInTeamChallenge && stats[0] <= 6) {
        this.translate.get('PLAYER.ALERT.scoredPoints', { some: stats[0], all: 6 }).subscribe((message: string) => {
          this.snackBar.open(message, null, {
            verticalPosition: 'top',
            duration: 4000,
            panelClass: ['b2a-success', 'top'],
          });
        });
      }
    });
  }

  getNextExercise() {
    if (this.program.exercises.length > this.currentExerciseIndex + 1) {
      this.currentExerciseIndex += 1;

      this.nextExerciseCalled = true;

      this.videoPlayer.changeUrl(
        this.program.exercises[this.currentExerciseIndex].videoUrl['de'],
        this.program.exercises[this.currentExerciseIndex].captionUrl[this.translate.currentLang]
      );
    } else {
      this.closeDialog();
    }
    this.didSentStatistic = false;
  }

  getNewExercise() {
    this.closeDialog({ newRandom: true });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  closeDialog(result?: any): void {
    this.dialogRef.close(result);
  }

  public buttonClicked: boolean = false;

  openAddDialog(event: any): void {
    if (this.videoPlayer && this.videoPlayer.fullScreened) {
      this.videoPlayer.toggleFullScreen();
    }

    event.stopPropagation();

    const dialogRef = this.dialog.open(AddDialogComponent, {
      width: '90vw',
      height: '400px',
      data: { exerciseID: this.program.exercises[this.currentExerciseIndex] },
    });
  }

  openSuccessDialog(achievement: Achievement): void {
    if (this.videoPlayer && this.videoPlayer.fullScreened) {
      this.videoPlayer.toggleFullScreen();
    }

    console.log('Success Dialog opened');
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: '400px',
      height: '400px',
      data: { achievement: achievement },
    });

    this.closeDialog();
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/theme/components/videoDialog/videoDialog.component.ts